import React, { useEffect } from 'react';
import { 
  Phone, Mail, MapPin, Clock, Car,
  Building2, Landmark, Hotel, MessageCircle, Navigation
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const ClinicaAlameda = () => {
  const navigate = useNavigate();
  const lang = useLanguage();
  const t = getPageTexts('clinicaAlameda', lang);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const hoteles = [
    {
      nombre: "Hotel Lima Marbella",
      estrellas: 4,
      descripcion: lang === 'es' ? "A 3 min de la playa y centro histórico" : 
                   lang === 'en' ? "3 min from beach and historic center" :
                   lang === 'fr' ? "À 3 min de la plage et du centre historique" :
                   "A 3 min da praia e centro histórico",
      precio: lang === 'es' ? "Desde 100-120 € / noche" : 
              lang === 'en' ? "From €100-120 / night" :
              lang === 'fr' ? "À partir de 100-120 € / nuit" :
              "A partir de €100-120 / noite",
      telefono: "+34 952 770 500"
    },
    {
      nombre: "Óbal Hotel",
      estrellas: 4,
      descripcion: lang === 'es' ? "Junto a la Alameda y casco histórico" :
                   lang === 'en' ? "Next to Alameda and historic center" :
                   lang === 'fr' ? "À côté de l'Alameda et du centre historique" :
                   "Junto à Alameda e centro histórico",
      precio: lang === 'es' ? "Desde 95-160 € / noche" :
              lang === 'en' ? "From €95-160 / night" :
              lang === 'fr' ? "À partir de 95-160 € / nuit" :
              "A partir de €95-160 / noite",
      telefono: "+34 952 771 250"
    },
    {
      nombre: "Hotel Monarque El Rodeo",
      estrellas: 3,
      descripcion: lang === 'es' ? "Centro de Marbella, muy práctico" :
                   lang === 'en' ? "Marbella center, very practical" :
                   lang === 'fr' ? "Centre de Marbella, très pratique" :
                   "Centro de Marbella, muito prático",
      precio: lang === 'es' ? "Desde 70-100 € / noche" :
              lang === 'en' ? "From €70-100 / night" :
              lang === 'fr' ? "À partir de 70-100 € / nuit" :
              "A partir de €70-100 / noite",
      telefono: "+34 952 775 100"
    },
    {
      nombre: "Ona Princesa Playa",
      estrellas: 4,
      tipo: "Aparthotel",
      descripcion: lang === 'es' ? "Paseo Marítimo, frente al mar" :
                   lang === 'en' ? "Seafront promenade, facing the sea" :
                   lang === 'fr' ? "Promenade maritime, face à la mer" :
                   "Passeio Marítimo, frente ao mar",
      precio: lang === 'es' ? "Desde 55-120 € / noche" :
              lang === 'en' ? "From €55-120 / night" :
              lang === 'fr' ? "À partir de 55-120 € / nuit" :
              "A partir de €55-120 / noite",
      telefono: "+34 951 127 529"
    }
  ];

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      {/* Hero Section with Map */}
      <section className="relative">
        {/* Google Maps Embed */}
        <div className="w-full h-[350px] relative">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1597.5!2d-4.8853192!3d36.508194!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd7327f8604a4f9b%3A0x134493038ea2c9d0!2sC.%20Carlos%20Mackintosh%2C%203%2C%2029602%20Marbella%2C%20M%C3%A1laga!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'grayscale(30%)' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Clínica Estética Alameda - Marbella"
          ></iframe>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ivory via-ivory/80 to-transparent h-32"></div>
        </div>

        {/* Clinic Card Overlay */}
        <div className="container mx-auto px-6 -mt-20 relative z-10">
          <div className="max-w-4xl mx-auto bg-ivory border border-divider p-10 animate-on-scroll">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 border border-gold flex items-center justify-center">
                    <Building2 className="text-gold" size={24} />
                  </div>
                  <div>
                    <h1 className="font-serif text-3xl text-charcoal">
                      {t.clinicName}
                    </h1>
                    <p className="text-text-muted text-sm">{t.centerMarbella}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <MapPin className="text-gold flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.address}</p>
                      <p className="text-charcoal">C/ Carlos Mackintosh, 3, 5º, 3ª</p>
                      <p className="text-text-secondary">29602 Marbella, Málaga</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Phone className="text-gold flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.phone}</p>
                      <a href="tel:+34952764547" className="text-gold hover:text-gold/70 transition-colors">
                        +34 952 76 45 47
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Clock className="text-gold flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.schedule}</p>
                      <p className="text-charcoal">{t.scheduleWeekdays}</p>
                      <p className="text-text-secondary">{t.scheduleSaturday}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:w-48">
                <a 
                  href="https://www.google.com/maps/place/C.+Carlos+Mackintosh,+3,+29602+Marbella,+M%C3%A1laga/@36.508194,-4.8853192,17z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold text-center flex items-center justify-center gap-2"
                >
                  <Navigation size={16} />
                  {t.howToGet}
                </a>
                <a 
                  href="tel:+34952764547"
                  className="btn-outline-gold text-center flex items-center justify-center gap-2"
                >
                  <Phone size={16} />
                  {t.call}
                </a>
                <a 
                  href="https://wa.me/34952764547"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dark text-center flex items-center justify-center gap-2"
                >
                  <MessageCircle size={16} />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About the Location */}
      <section className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16 animate-on-scroll">
            <p className="eyebrow mb-6">{t.locationTitle}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-6">
              {t.locationSubtitle}
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t.locationDesc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Avenida del Mar / Dalí */}
            <div className="card-luxury p-8 animate-on-scroll">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-gold flex items-center justify-center flex-shrink-0">
                  <Landmark className="text-gold" size={20} />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-charcoal mb-3">
                    {t.avenueName}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: t.avenueDesc }} />
                </div>
              </div>
            </div>

            {/* Centro Histórico */}
            <div className="card-luxury p-8 animate-on-scroll">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-gold flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-gold" size={20} />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-charcoal mb-3">
                    {t.historicCenter}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: t.historicCenterDesc }} />
                </div>
              </div>
            </div>
          </div>

          {/* Parking Info */}
          <div className="bg-cream p-8 animate-on-scroll">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 border border-gold flex items-center justify-center flex-shrink-0">
                <Car className="text-gold" size={20} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-charcoal mb-3">
                  {t.parking}
                </h3>
                <p className="text-text-secondary leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t.parkingDesc }} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-charcoal font-medium">{t.parkingName}</p>
                    <p className="text-text-secondary">{t.parkingLocation}</p>
                  </div>
                  <div>
                    <p className="text-charcoal font-medium">{t.parkingRate}</p>
                    <p className="text-text-secondary">{t.parkingPrice}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hotels Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16 animate-on-scroll">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Hotel className="text-gold" size={24} />
              <p className="eyebrow">{t.hotelsTitle}</p>
            </div>
            <h2 className="font-serif text-3xl text-charcoal mb-6">
              {t.hotelsSubtitle}
            </h2>
            <p className="text-text-secondary max-w-xl mx-auto">
              {t.hotelsDesc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {hoteles.map((hotel, index) => (
              <div key={index} className="card-luxury p-6 animate-on-scroll">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-serif text-lg text-charcoal">{hotel.nombre}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      {[...Array(hotel.estrellas)].map((_, i) => (
                        <span key={i} className="text-gold">★</span>
                      ))}
                      {hotel.tipo && (
                        <span className="text-xs text-text-muted ml-2">{hotel.tipo}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <p className="text-text-secondary text-sm mb-4">{hotel.descripcion}</p>
                
                <div className="flex items-center justify-between pt-4 border-t border-divider">
                  <span className="text-gold text-sm font-medium">{hotel.precio}</span>
                  <a 
                    href={`tel:${hotel.telefono.replace(/\s/g, '')}`}
                    className="flex items-center gap-1 text-text-muted hover:text-gold text-sm transition-colors"
                  >
                    <Phone size={12} />
                    {hotel.telefono}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-3xl text-center animate-on-scroll">
          <p className="eyebrow text-gold mb-6">{t.ctaTitle}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-ivory mb-6">
            {t.ctaSubtitle}
          </h2>
          <p className="text-gold-light/70 mb-10 max-w-xl mx-auto">
            {t.ctaDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+34952764547"
              className="btn-gold inline-flex items-center justify-center gap-2"
            >
              <Phone size={18} />
              +34 952 76 45 47
            </a>
            <a 
              href="mailto:info@drangelnorro.com"
              className="btn-outline-gold border-gold/50 text-gold hover:bg-gold hover:text-charcoal inline-flex items-center justify-center gap-2"
            >
              <Mail size={18} />
              info@drangelnorro.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ClinicaAlameda;
